import { apiFetch, tokens } from "./client.ts";

export type CallStatus =
  | "queued"
  | "ringing"
  | "in_progress"
  | "completed"
  | "busy"
  | "failed"
  | "no_answer"
  | "canceled"
  | "voicemail";

export interface Call {
  id: string;
  account_id: string;
  site_id: string | null;
  twilio_call_sid: string;
  direction: "inbound" | "outbound";
  status: CallStatus;
  from_e164: string;
  to_e164: string;
  forwarded_to_e164: string | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration_s: number | null;
  client_id: string | null;
  lead_id: string | null;
  recording_id: string | null;
}

export interface CallList {
  items: Call[];
  total: number;
  limit: number;
  offset: number;
}

export interface Recording {
  id: string;
  twilio_recording_sid: string;
  duration_s: number | null;
  status: "pending" | "archived" | "failed" | "deleted";
  archived_at: string | null;
  size_bytes: number | null;
}

export interface SignedUrl {
  url: string;
  expires_in: number;
}

export interface Transcript {
  id: string;
  call_id: string | null;
  recording_id: string | null;
  provider: "twilio" | "claude" | "whisper";
  status: "pending" | "processing" | "done" | "failed";
  language: string | null;
  raw_text: string | null;
  summary: string | null;
  action_items: string[] | null;
  sentiment: "positive" | "neutral" | "negative" | "urgent" | null;
  created_at: string;
  updated_at: string;
}

export interface ClickToCallPayload {
  site_id: string;
  to_number: string;
  dispatcher_number?: string;
  lead_id?: string;
  client_id?: string;
}

export interface ClickToCallResult {
  call_sid: string;
  status: string;
  message: string;
}

export const listCalls = (params: {
  status?: CallStatus;
  q?: string;
  site_id?: string;
  limit?: number;
  offset?: number;
}) => {
  const usp = new URLSearchParams();
  if (params.status) usp.set("status", params.status);
  if (params.q) usp.set("q", params.q);
  if (params.site_id) usp.set("site_id", params.site_id);
  usp.set("limit", String(params.limit ?? 50));
  usp.set("offset", String(params.offset ?? 0));
  return apiFetch<CallList>(`/calls?${usp}`);
};

export const getCall = (id: string) => apiFetch<Call>(`/calls/${id}`);
export const getCallRecording = (id: string) => apiFetch<Recording>(`/calls/${id}/recording`);
export const getRecordingSignedUrl = (id: string) => apiFetch<SignedUrl>(`/calls/${id}/recording/signed-url`);

export const getCallTranscript = (callId: string) =>
  apiFetch<Transcript>(`/calls/${callId}/transcript`);

export const clickToCall = (payload: ClickToCallPayload) =>
  apiFetch<ClickToCallResult>("/calls/click-to-call", {
    method: "POST",
    body: JSON.stringify(payload),
  });

/**
 * Stream the recording audio through the backend (auth-attached) and return a
 * blob URL the `<audio>` element can play. Caller must `URL.revokeObjectURL`
 * when unmounting.
 */
export async function fetchRecordingBlobUrl(callId: string): Promise<string> {
  const resp = await fetch(`/api/v1/calls/${callId}/recording/audio`, {
    headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`audio fetch failed (${resp.status}): ${text}`);
  }
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

export interface LeadExtraction {
  phone_e164: string | null;
  name: string | null;
  address_text: string | null;
  property_type: "residential" | "commercial" | null;
  service_type: string | null;
  gate_operator_kind: string | null;
  job_details: string | null;
  scheduled_time: string | null;
  service_call_fee_cents: number | null;
  notes: string | null;
}

export const extractLeadFields = (callId: string) =>
  apiFetch<LeadExtraction>(`/calls/${callId}/lead-extraction`);
